import { fetchWithFallback } from "../OrgUsageAnalytics/api";

const BASE = "/integrations/lendingpad";

export async function listLendingPadOrganizations(env, params = {}) {
  return fetchWithFallback(`${BASE}/organizations`, params, { env });
}

// Searches ALL organizations (even ones with no LendingPad connection yet) so
// Operations can find an org and set it up / activate it manually. Reuses the
// lightweight org directory endpoint already used by OrgPicker.
export async function searchOrganizations(env, search) {
  return fetchWithFallback(
    "/organizations/usage-limits",
    { search },
    { env, timeoutMs: 30000 },
  );
}

// Without a companyId the backend describes the organization's primary
// LendingPad company and says which one in `selectedCompanyId`.
export async function getLendingPadOrganization(env, organizationId, companyId) {
  return fetchWithFallback(
    `${BASE}/organizations/${organizationId}`,
    { companyId },
    { env },
  );
}

export async function saveLendingPadConfig(env, body) {
  return fetchWithFallback(`${BASE}/save-config`, {}, { env, method: "POST", body });
}

// Sets which LendingPad stages the organization imports. Body:
// { userId, organizationId, syncedStatusIds: number[]|null } — `null` resets to
// the global default. Newly enabled stages trigger a one-time backfill server-side.
export async function saveLendingPadSyncStages(env, body) {
  return fetchWithFallback(
    `${BASE}/sync-stages`,
    {},
    { env, method: "POST", body },
  );
}

// Sets whose LendingPad loans the organization imports. Body:
// { userId, organizationId, importScope: "organization_team"|"all_loans" }.
// Widening to every loan triggers a backfill of the imported stages server-side.
export async function saveLendingPadImportScope(env, body) {
  return fetchWithFallback(
    `${BASE}/import-scope`,
    {},
    { env, method: "POST", body },
  );
}

// Replaces the LendingPad folders whose documents the organization does not
// import. Body: { userId, ignoredDocumentFolders: string[]|null }. Names match
// LendingPad exactly; `null` clears the list. Only future imports are affected.
export async function saveLendingPadIgnoredFolders(env, organizationId, body) {
  return fetchWithFallback(
    `${BASE}/organizations/${encodeURIComponent(organizationId)}/ignored-folders`,
    {},
    { env, method: "PUT", body },
  );
}

// Selects a LendingPad loan by its loan number. Body: { userId, loanNumber }.
// Answers 201 when added, 200 when it was already selected. In the
// `selected_loans` import scope a loan not in Addy yet is imported right away.
export async function addLendingPadSelectedLoan(env, organizationId, body) {
  return fetchWithFallback(
    `${BASE}/organizations/${encodeURIComponent(organizationId)}/selected-loans`,
    {},
    { env, method: "POST", body },
  );
}

// Unselects a LendingPad loan; the loan stays in Addy. The admin userId goes in
// the query, which fetchWithFallback adds to every call.
export async function removeLendingPadSelectedLoan(
  env,
  organizationId,
  lendingPadLoanId,
  companyId,
) {
  return fetchWithFallback(
    `${BASE}/organizations/${encodeURIComponent(organizationId)}/selected-loans/${encodeURIComponent(lendingPadLoanId)}`,
    { companyId },
    { env, method: "DELETE" },
  );
}

// Adds LendingPad identities to the organization owner/member roster. Body:
// { userId, organizationId, teamEmails: string[]|null } — `null` clears only
// the additional addresses. Newly declared people trigger a backfill of their
// existing loans server-side.
export async function saveLendingPadTeamEmails(env, body) {
  return fetchWithFallback(
    `${BASE}/team-emails`,
    {},
    { env, method: "POST", body },
  );
}

export async function testLendingPadConnection(env, body) {
  return fetchWithFallback(
    `${BASE}/test-connection`,
    {},
    { env, method: "POST", body, timeoutMs: 90000 },
  );
}

export async function activateLendingPad(env, body) {
  return fetchWithFallback(`${BASE}/activate`, {}, { env, method: "POST", body });
}

export async function retryLendingPadActivation(env, body) {
  return fetchWithFallback(
    `${BASE}/retry-activation`,
    {},
    { env, method: "POST", body, timeoutMs: 90000 },
  );
}

export async function disableLendingPad(env, body) {
  return fetchWithFallback(`${BASE}/disable`, {}, { env, method: "POST", body });
}

export async function retryLendingPadBackfill(env, body) {
  return fetchWithFallback(
    `${BASE}/retry-backfill`,
    {},
    { env, method: "POST", body, timeoutMs: 120000 },
  );
}

export async function listLendingPadActivity(env, organizationId, params = {}) {
  return fetchWithFallback(
    `${BASE}/organizations/${organizationId}/activity`,
    params,
    { env },
  );
}

export async function listLendingPadSyncHistory(env, organizationId, params = {}) {
  return fetchWithFallback(
    `${BASE}/organizations/${organizationId}/sync-history`,
    params,
    { env },
  );
}

// Links one more LendingPad company to the organization. Body: { userId,
// companyId, lendingPadUserId, alias? }.
export async function addLendingPadCompany(env, organizationId, body) {
  return fetchWithFallback(
    `${BASE}/organizations/${encodeURIComponent(organizationId)}/companies`,
    {},
    { env, method: "POST", body },
  );
}

// Body: { userId, alias?, companyId?, lendingPadUserId? }. `alias` null clears
// it; the Company ID only changes before the company is approved.
export async function updateLendingPadCompany(env, organizationId, companyId, body) {
  return fetchWithFallback(
    `${BASE}/organizations/${encodeURIComponent(organizationId)}/companies/${encodeURIComponent(companyId)}`,
    {},
    { env, method: "PATCH", body },
  );
}

// Real totals (sync history split by trigger, loans, documents) for the counts
// shown next to the detail panel. Separate from the detail call so a slow or
// failing aggregation never blocks the panel from rendering.
export async function getLendingPadStats(env, organizationId, companyId) {
  return fetchWithFallback(
    `${BASE}/organizations/${organizationId}/stats`,
    { companyId },
    { env },
  );
}

// Read-only diff against LendingPad's live data: what changed there that never
// reached us. Walks one paginated list per imported stage, so it is slow by
// nature — hence the long timeout. Performs GETs only; imports nothing.
export async function reconcileLendingPad(env, organizationId, companyId) {
  return fetchWithFallback(
    `${BASE}/organizations/${organizationId}/reconcile`,
    { companyId },
    { env, timeoutMs: 180000 },
  );
}

// Returns the exact vendor support-request text (built by the same backend
// template that emails LendingPad support).
export async function getLendingPadSupportRequest(env, organizationId, companyId) {
  return fetchWithFallback(
    `${BASE}/organizations/${organizationId}/support-request`,
    { companyId },
    { env },
  );
}

// Everyone who matters to the integration, one row each: the owner and every
// accepted member with how many loans they are on and their resolved
// visibility, the declared LendingPad addresses with their loan count, and the
// import scope. Loaded lazily on the People tab: it scans organization-members
// and runs one count per person, so it must never sit in the critical path of
// the detail panel.
export async function listLendingPadPeople(env, organizationId, companyId) {
  return fetchWithFallback(
    `${BASE}/organizations/${organizationId}/people`,
    { companyId },
    { env },
  );
}

// Read-only: why one person can or cannot open one loan, using the verdict the
// loan board itself applies. `loan` is an Addy loan id, a lender loan number or
// a LendingPad id; a loan that never reached Addy comes back with the last
// sync-history entry recorded for it.
export async function explainLendingPadAccess(
  env,
  organizationId,
  { subjectType, memberId, loan },
) {
  return fetchWithFallback(
    `${BASE}/organizations/${organizationId}/explain-access`,
    { subjectType, ...(memberId ? { memberId } : {}), loan },
    { env },
  );
}

// Assigns which of the roles LendingPad granted this person decides their loan
// visibility in one LendingPad company. Body: { userId, organizationId,
// subjectType, memberId?, companyId?, opsRoleSelection } — a role type from
// their capture in that company, the sentinel "own_loans_only", or null to fall
// back to the automatic least-privilege rule.
export async function setLendingPadRoleSelection(env, body) {
  return fetchWithFallback(
    `${BASE}/set-role-selection`,
    {},
    { env, method: "POST", body },
  );
}

// Records that an LOS address belongs to this person (users.losEmails), so
// every loan carrying it opens for them. One address belongs to one Addy user
// at a time; a 409 names who has it. Body: { userId, organizationId,
// subjectType, memberId?, email }
export async function linkLendingPadLosEmail(env, body) {
  return fetchWithFallback(
    `${BASE}/link-los-email`,
    {},
    { env, method: "POST", body },
  );
}

// Removes a linked address. A declared team address stays declared: import
// scope and visibility are separate layers. Body as link.
export async function unlinkLendingPadLosEmail(env, body) {
  return fetchWithFallback(
    `${BASE}/unlink-los-email`,
    {},
    { env, method: "POST", body },
  );
}

// Break-glass: deletes a stored capture so LendingPad stops scoping this person
// until a new one is taken. Deliberately not durable — the capture returns on
// their next LendingPad visit, so this unblocks someone today without becoming
// a grant. Body: { userId, organizationId, subjectType, memberId?, companyId? }
export async function clearLendingPadRoleCapture(env, body) {
  return fetchWithFallback(
    `${BASE}/clear-role-capture`,
    {},
    { env, method: "POST", body },
  );
}
